import { Avatar, Dropdown, Empty, Form, Input, Menu, Modal, notification, Select, Spin } from "antd";
import { MoreOutlined, UserOutlined } from "@ant-design/icons";
import esES from "antd/lib/locale/es_ES";
import { getAllRoles, getAllUsers, postUser, putUser } from "api/private";
import { CommonSearchForm, InternalPageLayout, Table } from "components";
import { usePermissions } from "hooks";
import { useEffect, useState } from "react";

interface Role {
    id: string;
    name: string;
}

interface User {
    id: string;
    email: string;
    lastnames?: string;
    name: string;
    photo_uri?: string;
    profile?: string;
    role_id?: string;
    role?: { id?: string; name?: string };
    zone?: unknown[];
}

const profileOptions = [
    { value: "admin", label: "Admin" },
    { value: "callcenter", label: "Call center" }
];

const getUserRoleId = (user: User) => user.role_id || user.role?.id;

const Usersoperator = () => {
    const { can } = usePermissions();
    const canWrite = can("users", "write");
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [form] = Form.useForm();

    const loadUsers = async () => {
        setIsLoading(true);
        const response: any = await getAllUsers();
        if (response.success === "true") {
            setUsers(response.data || []);
        } else {
            notification.error({
                message: "Error",
                description: response.errors || "No se pudieron cargar los usuarios"
            });
        }
        setIsLoading(false);
    };

    const loadRoles = async () => {
        const response: any = await getAllRoles();
        if (response.success === "true") {
            setRoles(response.data || []);
        } else {
            notification.error({
                message: "Error",
                description: response.errors || "No se pudieron cargar los roles"
            });
        }
    };

    useEffect(() => {
        loadUsers();
        loadRoles();
    }, []);

    const openAddModal = () => {
        setEditingUser(null);
        form.resetFields();
        setModalVisible(true);
    };

    const openEditModal = (user: User) => {
        setEditingUser(user);
        form.setFieldsValue({
            ...user,
            profile: user.profile === "call center" || user.profile === "call_center"
                ? "callcenter"
                : user.profile,
            role_id: getUserRoleId(user)
        });
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setEditingUser(null);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setIsSaving(true);
            const payload = {
                name: values.name.trim(),
                lastnames: values.lastnames?.trim() || null,
                email: values.email.trim(),
                photo_uri: values.photo_uri?.trim() || null,
                profile: values.profile,
                role_id: values.role_id
            };
            const response: any = editingUser
                ? await putUser(editingUser.id, { user: { id: editingUser.id, ...payload } })
                : await postUser({ user: { ...payload, password: values.password } });

            if (response.success !== "true") {
                throw new Error(response.errors || "No se pudo guardar el usuario");
            }

            notification.success({ message: "Usuario guardado" });
            closeModal();
            await loadUsers();
        } catch (error: any) {
            if (error?.errorFields) return;
            notification.error({ message: "Error", description: error.message || "No se pudo guardar el usuario" });
        } finally {
            setIsSaving(false);
        }

    };

    const filteredUsers = users.filter((user) => (
        `${user.name} ${user.lastnames || ""} ${user.email} ${user.profile || ""}`
            .toLowerCase()
            .includes(search.toLowerCase())
    ));

    const columns = [
        {
            title: "Avatar",
            dataIndex: "photo_uri",
            key: "photo_uri",
            render: (photoUri: string, user: User) => (
                <Avatar src={photoUri} icon={<UserOutlined />} alt={user.name} />
            )
        },
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
            render: (name: string, user: User) => `${name} ${user.lastnames || ""}`.trim(),
            sorter: (first: User, second: User) => first.name.localeCompare(second.name)
        },
        { title: "Correo", dataIndex: "email", key: "email" },
        {
            title: "Perfil",
            dataIndex: "profile",
            key: "profile",
            render: (profile: string) => profileOptions.find(({ value }) => value === profile)?.label || profile || "—"
        },
        {
            title: "Rol",
            key: "role",
            render: (_value: unknown, user: User) => (
                user.role?.name || roles.find(({ id }) => id === getUserRoleId(user))?.name || "Sin rol"
            )
        },
        {
            title: "Acciones",
            key: "actions",
            render: (_value: unknown, user: User) => {
                if (!canWrite) return null;
                return (
                    <Dropdown
                        overlay={(
                            <Menu
                                items={[{
                                    key: "edit",
                                    label: "Editar",
                                    onClick: () => openEditModal(user)
                                }]}
                            />
                        )}
                        trigger={["click"]}
                    >
                        <MoreOutlined />
                    </Dropdown>
                );
            }
        }
    ];

    return (
        <InternalPageLayout title="Operadores">
            <Spin spinning={isLoading}>
                <CommonSearchForm
                    clickAdd={openAddModal}
                    search={search}
                    onSearch={({ target }: any) => setSearch(target.value)}
                    canAdd={canWrite}
                />
                <Table
                    localeProv={esES}
                    size="small"
                    rowKey="id"
                    columns={columns}
                    dataSource={filteredUsers}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay usuarios" /> }}
                    scroll={{ x: "auto" }}
                    pagination={{ defaultPageSize: 10 }}
                    loading={isLoading}
                />
            </Spin>
            <Modal
                title={editingUser ? "Editar usuario" : "Agregar usuario"}
                visible={modalVisible}
                onCancel={closeModal}
                onOk={handleSubmit}
                confirmLoading={isSaving}
                okText="Guardar"
                cancelText="Cancelar"
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Nombre"
                        name="name"
                        rules={[
                            { required: true, message: "Ingresa un nombre" },
                            { pattern: /^[A-Za-z�-�\s]+$/, message: "El nombre no puede contener n�meros" }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Apellidos"
                        name="lastnames"
                        rules={[{ pattern: /^[A-Za-z�-�\s]+$/, message: "Los apellidos no pueden contener n�meros" }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item label="Correo" name="email" rules={[{ required: true, type: "email", message: "Ingresa un correo v�lido" }]}>
                        <Input />
                    </Form.Item>
                    {!editingUser && (
                        <Form.Item
                            label="Contrase�a"
                            name="password"
                            rules={[
                                { required: true, message: "Ingresa una contrase�a" },
                                { min: 8, message: "La contrase�a debe tener al menos 8 caracteres" }
                            ]}
                        >
                            <Input.Password />
                        </Form.Item>
                    )}
                    <Form.Item label="Perfil" name="profile" rules={[{ required: true, message: "Selecciona un perfil" }]}>
                        <Select options={profileOptions} />
                    </Form.Item>
                    <Form.Item label="Rol" name="role_id" rules={[{ required: true, message: "Selecciona un rol" }]}>
                        <Select options={roles.map((role) => ({ value: role.id, label: role.name }))} />
                    </Form.Item>
                    <Form.Item label="Avatar (URL)" name="photo_uri" rules={[{ type: "url", message: "Ingresa una URL v�lida" }]}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </InternalPageLayout>
    );
};

export default Usersoperator;
